
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, AppNotification } from '../state/NotificationContext';

export const NotificationCenter = React.memo(() => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = (notification: AppNotification) => {
        markAsRead(notification.id);
        if (notification.linkTo) {
            navigate(notification.linkTo);
            setIsOpen(false);
        }
    };

    const getIcon = (type: string) => {
        switch(type) {
            case 'order': return 'local_shipping';
            case 'promo': return 'local_offer';
            case 'social': return 'public';
            default: return 'notifications';
        }
    };

    const getColor = (type: string) => {
        switch(type) {
            case 'order': return 'text-blue-500 bg-blue-100 dark:bg-blue-900/20';
            case 'promo': return 'text-orange-500 bg-orange-100 dark:bg-orange-900/20';
            case 'social': return 'text-purple-500 bg-purple-100 dark:bg-purple-900/20';
            default: return 'text-gray-500 bg-gray-100 dark:bg-white/10';
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="relative w-10 h-10 rounded-full hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
            >
                <span className={`material-symbols-outlined text-[24px] ${isOpen ? 'filled text-primary' : 'text-text-main dark:text-white'}`}>notifications</span>
                {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background-light dark:border-background-dark animate-pulse"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-12 w-80 md:w-96 bg-surface-light dark:bg-[#1c1917] backdrop-blur-xl border border-black/5 dark:border-white/10 rounded-2xl shadow-2xl z-[80] overflow-hidden animate-slide-up origin-top-right">
                    <div className="flex justify-between items-center p-4 border-b border-black/5 dark:border-white/5 bg-gray-50/50 dark:bg-white/5">
                        <h3 className="font-bold text-sm text-text-main dark:text-white">Notificaciones</h3>
                        <div className="flex gap-2">
                            {unreadCount > 0 && (
                                <button onClick={markAllAsRead} className="text-[10px] font-bold text-primary hover:underline">
                                    Marcar leídas
                                </button>
                            )}
                            <button onClick={clearNotifications} className="text-[10px] text-gray-400 hover:text-red-500">
                                Limpiar
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto no-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center px-6">
                                <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">notifications_off</span>
                                <p className="text-xs text-gray-400">No tienes notificaciones nuevas.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-white/5">
                                {notifications.map(notif => (
                                    <div 
                                        key={notif.id}
                                        onClick={() => handleNotificationClick(notif)}
                                        className={`p-4 flex gap-3 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors relative ${!notif.read ? 'bg-primary/5' : ''}`}
                                    >
                                        {!notif.read && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>}
                                        
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getColor(notif.type)}`}>
                                            <span className="material-symbols-outlined text-xl">{getIcon(notif.type)}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <p className={`text-xs font-bold leading-tight ${!notif.read ? 'text-text-main dark:text-white' : 'text-text-secondary dark:text-gray-400'}`}>
                                                    {notif.title}
                                                </p>
                                                <span className="text-[9px] text-gray-400 whitespace-nowrap ml-2">
                                                    {new Date(notif.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </span>
                                            </div>
                                            <p className="text-xs text-text-secondary dark:text-gray-500 mt-1 line-clamp-2">
                                                {notif.message}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

NotificationCenter.displayName = 'NotificationCenter';
